import { useQuery } from "@tanstack/react-query";
import { Crown, Flame, Trophy, Users } from "lucide-react";
import { fetchFeaturedFans } from "@/lib/predictions/predictionReputation";
import { reputationLevelFor } from "@/lib/predictions/reputationLevels";
import { useLanguage } from "@/hooks/useLanguage";

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? "F").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase();
}

/**
 * Featured Fans — public ranking panel powered by the get_featured_fans
 * Postgres function. Shows top predictors with avatar, level, streak and
 * favorite team. Falls back to an empty state when no rows exist yet.
 */
export function FeaturedFansPanel({ limit = 10 }: { limit?: number }) {
  const { t } = useLanguage();
  const { data, isLoading } = useQuery({
    queryKey: ["featured-fans", limit],
    queryFn: () => fetchFeaturedFans(limit),
    staleTime: 60_000,
  });

  return (
    <section className="fan-identity-frame rounded-2xl bg-background/60 p-4 backdrop-blur">
      <header className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Crown className="h-5 w-5 text-amber-500" />
          <h2 className="text-sm font-black uppercase tracking-wider">
            {t("featured_fans_title")}
          </h2>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-muted-foreground">
          <Users className="h-3 w-3" /> {t("featured_fans_subtitle")}
        </span>
      </header>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-accent/20" />
          ))}
        </div>
      ) : !data || data.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 bg-accent/10 p-4 text-center text-xs text-muted-foreground">
          {t("featured_fans_empty")}
        </div>
      ) : (
        <ol className="space-y-1.5">
          {data.map((fan, idx) => {
            const { current } = reputationLevelFor(fan.total_points);
            const rankBadge =
              idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `${idx + 1}.`;
            return (
              <li
                key={fan.user_id}
                className="flex items-center gap-2.5 rounded-xl border border-border/40 bg-accent/10 p-2"
              >
                <span className="w-6 shrink-0 text-center text-sm font-black">
                  {rankBadge}
                </span>
                {fan.avatar_url ? (
                  <img
                    src={fan.avatar_url}
                    alt=""
                    loading="lazy"
                    className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-border/60"
                  />
                ) : (
                  <div
                    aria-hidden
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-black text-primary"
                  >
                    {initialsFor(fan.display_name)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 truncate text-[13px] font-bold">
                    <span className="truncate">{fan.display_name}</span>
                    <span aria-hidden>{current.emoji}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span style={{ color: current.color }}>{current.label}</span>
                    {fan.favorite_team && <span>· {fan.favorite_team}</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-0.5 text-[11px] font-bold">
                  <span className="inline-flex items-center gap-1">
                    <Trophy className="h-3 w-3 text-primary" /> {fan.total_points}
                  </span>
                  {fan.streak_best > 1 && (
                    <span className="inline-flex items-center gap-1 text-amber-500">
                      <Flame className="h-3 w-3" /> {fan.streak_best}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}